(function () {
  "use strict";

  /* ============================================================
     GALLERY START-INDEX FALLBACK MAP (centralized — this is the only
     place this mapping lives)
     Preferred source is still the Liquid-computed `data.startIndex`, built
     server-side from the variant.metafields.custom.gallery_start_index
     metafield (see sections/main-product-sleep.liquid). That requires a
     merchant to create the metafield definition and fill it in per variant
     in Shopify Admin, which hasn't happened yet on the live product, so the
     server always sends startIndex: 0 right now. Until then, this map is
     the working fallback for Nubea Eclipse's current product.media layout
     (Clásico starts at image 1, Negro Carbón at 8, Rosa Amanecer at 15 —
     1-based, as configured/communicated; converted to 0-based here).
     Keyed by option1 (the real Shopify option value string), matching
     exactly what's already embedded per variant in SleepVariantData. Only
     ever used when the metafield-derived startIndex is 0/unset, so setting
     up the metafield later overrides this automatically with zero code
     changes — remove this map at that point.
     ============================================================ */
  var GALLERY_START_INDEX_FALLBACK = {
    "Clásico": 0,
    "Negro Carbón": 7,
    "Rosa Amanecer": 14,
  };

  function resolveGalleryStartIndex(data) {
    if (data.startIndex) return data.startIndex;
    if (data.option1 && Object.prototype.hasOwnProperty.call(GALLERY_START_INDEX_FALLBACK, data.option1)) {
      return GALLERY_START_INDEX_FALLBACK[data.option1];
    }
    return 0;
  }

  function initAccordions(root) {
    root.querySelectorAll(".sleep-accordion__trigger:not([data-sleep-init])").forEach(function (trigger) {
      trigger.setAttribute("data-sleep-init", "true");
      trigger.addEventListener("click", function () {
        var expanded = trigger.getAttribute("aria-expanded") === "true";
        var panel = document.getElementById(trigger.getAttribute("aria-controls"));
        trigger.setAttribute("aria-expanded", String(!expanded));
        if (panel) panel.classList.toggle("is-open", !expanded);
      });
    });
  }

  /* ============================================================
     VARIANT SYNC (real Shopify variant is the source of truth)
     Source of data: the JSON embedded server-side by
     sections/main-product-sleep.liquid (id="SleepVariantData-{id}"), keyed by
     real Shopify variant id — price/availability/url plus the gallery
     resolved with the metafield > featured_media+product.media > product.media
     priority documented there. Applied synchronously on the swatch's own
     change event (no network wait), so hidden input[name="id"], price,
     Add to Cart state, URL and gallery change together, in the same tick —
     Dawn's own async Section Rendering fetch still runs after in parallel
     (SKU/inventory rules/quantity boundaries) but nothing that identifies
     "which variant is selected" depends on it finishing.
     ============================================================ */
  function parseVariantData(root) {
    var script = document.getElementById("SleepVariantData-" + root.dataset.section);
    if (!script) return {};
    try {
      return JSON.parse(script.textContent) || {};
    } catch (e) {
      return {};
    }
  }

  function galleryState(root) {
    if (!root.__sleepGalleryState) {
      root.__sleepGalleryState = { items: [], index: 0, variants: parseVariantData(root) };
    }
    return root.__sleepGalleryState;
  }

  /* option1 (Shopify's own value for this product's single option, e.g.
     "Negro Carbón") -> real variant id. Built once from the same variant
     data the gallery/price use, so a swatch never needs its own id
     precomputed correctly to select the right variant — matching plain
     text against plain text, no drop/positional guessing involved. */
  function optionValueVariantMap(root) {
    var state = galleryState(root);
    if (!state.optionValueMap) {
      var map = {};
      Object.keys(state.variants).forEach(function (variantId) {
        var option1 = state.variants[variantId].option1;
        if (option1) map[option1] = variantId;
      });
      state.optionValueMap = map;
    }
    return state.optionValueMap;
  }

  function renderThumbs(root, items) {
    var track = root.querySelector("[data-sleep-gallery-thumbs]");
    if (!track) return;
    track.innerHTML = "";
    items.forEach(function (item, i) {
      var li = document.createElement("li");
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "sleep-gallery__thumb" + (i === 0 ? " is-active" : "");
      btn.setAttribute("data-image", item.main);
      btn.setAttribute("data-index", String(i));
      var img = document.createElement("img");
      img.src = item.thumb || item.main;
      img.alt = item.alt || "";
      img.width = 120;
      img.height = 120;
      img.loading = "lazy";
      btn.appendChild(img);
      li.appendChild(btn);
      track.appendChild(li);
    });
  }

  function setActiveIndex(root, index) {
    var state = galleryState(root);
    if (!state.items.length) return;
    index = Math.max(0, Math.min(index, state.items.length - 1));
    state.index = index;
    var item = state.items[index];
    if (!item) return;

    var mainImage = root.querySelector(".sleep-gallery__main img");
    if (mainImage) {
      mainImage.style.opacity = "0";
      mainImage.src = item.main;
      mainImage.srcset = item.main2x ? item.main + " 1x, " + item.main2x + " 2x" : "";
      mainImage.alt = item.alt || mainImage.alt;
      requestAnimationFrame(function () {
        mainImage.style.opacity = "1";
      });
    }
    var mainBox = root.querySelector(".sleep-gallery__main");
    if (mainBox && item.ratio) mainBox.style.setProperty("--sleep-gallery-ratio", item.ratio);

    var activeThumb = null;
    root.querySelectorAll(".sleep-gallery__thumb").forEach(function (t) {
      var isActive = Number(t.getAttribute("data-index")) === index;
      t.classList.toggle("is-active", isActive);
      if (isActive) activeThumb = t;
    });
    if (activeThumb) {
      activeThumb.scrollIntoView({ block: "nearest", inline: "center" });
    }

    var total = state.items.length;
    var counter = root.querySelector("[data-sleep-gallery-counter]");
    if (counter) counter.textContent = (index + 1) + " / " + total;
  }

  function renderPrice(el, price, compareAtPrice) {
    if (!el) return;
    if (compareAtPrice) {
      el.innerHTML = "";
      var s = document.createElement("s");
      s.textContent = compareAtPrice;
      var sale = document.createElement("span");
      sale.className = "sleep-price--sale";
      sale.textContent = price;
      el.appendChild(s);
      el.appendChild(document.createTextNode(" "));
      el.appendChild(sale);
    } else {
      el.textContent = price;
    }
  }

  /* Hidden variant input, price (mobile + desktop), Add to Cart button state
     and the URL — the fields that answer "which variant is selected right
     now" — updated synchronously from the real Shopify variant data embedded
     for this variant id. */
  function applyVariantCore(root, variantId, data) {
    var sectionId = root.dataset.section;

    root.querySelectorAll('#product-form-' + sectionId + ' .product-variant-id, #product-form-installment-' + sectionId + ' input[name="id"]')
      .forEach(function (input) {
        input.value = variantId;
        input.disabled = !data.available;
      });

    renderPrice(document.getElementById("price-" + sectionId), data.price, data.compareAtPrice);
    renderPrice(document.getElementById("price-desktop-" + sectionId), data.price, data.compareAtPrice);

    var submitButton = document.getElementById("ProductSubmitButton-" + sectionId);
    if (submitButton) {
      submitButton.disabled = !data.available;
      var label = submitButton.querySelector("[data-sleep-cta-label]");
      if (label) {
        label.textContent = data.available
          ? "AÑADIR AL CARRITO"
          : (window.variantStrings && window.variantStrings.soldOut) || label.textContent;
      }
      var ctaPrice = submitButton.querySelector("[data-sleep-cta-price]");
      if (ctaPrice) ctaPrice.textContent = data.price;
    }

    if (root.dataset.updateUrl !== "false" && data.url) {
      /* history.replaceState can throw (e.g. SecurityError inside the
         Theme Editor's sandboxed preview iframe) — never let that abort
         the swatch click. Kept last and isolated so price/CTA/hidden input
         above are already applied regardless, and the gallery swap in
         applyVariant() (called right after this returns) still runs. */
      try {
        window.history.replaceState({}, "", data.url);
      } catch (e) {
        console.error("Sleep variant URL update failed:", e);
      }
    }
  }

  /* Full variant swap: hidden input/price/availability/CTA/URL (see
     applyVariantCore) plus the gallery — main image, thumbnails, counter —
     with nothing from the previous color left behind (no stale thumbs).
     Jumps to data.startIndex (0 for every variant except ones configured
     with custom.gallery_start_index — see the Liquid comment above the
     JSON this reads from) rather than always resetting to the first item. */
  function applyVariant(root, variantId) {
    var state = galleryState(root);
    var data = state.variants[String(variantId)];
    if (!data) return;

    applyVariantCore(root, variantId, data);

    var items = data.gallery;
    if (!items || !items.length) return;
    state.items = items;
    renderThumbs(root, items);
    setActiveIndex(root, resolveGalleryStartIndex(data));
    var gallery = root.querySelector("[data-sleep-gallery]");
    if (gallery) gallery.setAttribute("data-variant-id", variantId);
  }

  /* Selected-card border follows the real input state (input.checked),
     never a separately tracked index — so it can never drift from which
     variant is actually active, including on native radio-group behaviour
     (checking one swatch always unchecks the rest) and on deep links where
     Shopify already marks the right option `checked` server-side. */
  function updateSwatchSelectedState(root) {
    root.querySelectorAll(".sleep-color__options input[type=radio]").forEach(function (input) {
      var label = root.querySelector('label[for="' + input.id + '"]');
      if (label) label.classList.toggle("is-active", input.checked);
    });
  }

  function initGallery(root) {
    if (root.hasAttribute("data-sleep-gallery-init")) return;
    root.setAttribute("data-sleep-gallery-init", "true");

    /* Seed state from the server-rendered gallery for the initially selected
       variant (honours ?variant= deep links, since Shopify already resolves
       selected_or_first_available_variant from that param server-side). */
    var initialInput = root.querySelector('#product-form-' + root.dataset.section + ' input[name="id"]');
    var initialVariantId = initialInput && initialInput.value;
    if (initialVariantId) applyVariant(root, initialVariantId);
    updateSwatchSelectedState(root);

    var thumbTrack = root.querySelector("[data-sleep-gallery-thumbs]");
    if (thumbTrack) {
      thumbTrack.addEventListener("click", function (e) {
        var btn = e.target.closest(".sleep-gallery__thumb");
        if (!btn) return;
        setActiveIndex(root, Number(btn.getAttribute("data-index")));
      });
    }

    /* Color swatches are native radio+label pairs (Shopify variant-selects,
       data-variant-id is the real Shopify variant id resolved server-side).
       Delegate from the product-info root (survives variant-selects
       re-renders) so the whole variant — hidden input, price, availability,
       Add to Cart, URL and gallery — updates in the same synchronous tick.
       Dawn's own async Section Rendering fetch still runs in parallel
       afterwards for the fields this doesn't cover (SKU/inventory rules).

       Resolving which variant was picked: the input's own `.value` is the
       option text Liquid rendered (e.g. "Negro Carbón") — always correct,
       no matching involved. `optionValueVariantMap` looks that text up
       against each real variant's own `option1` (both embedded straight
       from Shopify data, nothing guessed) to get the real variant id. The
       swatch's `data-variant-id` is only a same-value fast path; if it were
       ever wrong for any reason, this lookup is what actually decides which
       variant loads — never a positional guess. */
    root.addEventListener("change", function (e) {
      var input = e.target.closest(".sleep-color__options input[data-option-value-id], .sleep-color__options input[data-variant-id]");
      if (!input) return;
      updateSwatchSelectedState(root);
      var variantId = optionValueVariantMap(root)[input.value] || input.getAttribute("data-variant-id");
      if (!variantId) {
        console.error("Sleep variant picker: no variant id resolved for", input.value);
        return;
      }
      try {
        applyVariant(root, variantId);
      } catch (err) {
        console.error("Sleep variant picker: applyVariant failed", err);
      }
    });

    var thumbPrev = root.querySelector("[data-thumb-prev]");
    var thumbNext = root.querySelector("[data-thumb-next]");
    if (thumbTrack && thumbPrev && thumbNext) {
      thumbPrev.addEventListener("click", function () { thumbTrack.scrollBy({ left: -80, behavior: "smooth" }); });
      thumbNext.addEventListener("click", function () { thumbTrack.scrollBy({ left: 80, behavior: "smooth" }); });
    }

    initSwipe(root);
  }

  /* ============================================================
     SWIPE (mobile) — pointer-based, reuses the existing single-image
     gallery (no parallel slider). touch-action: pan-y in CSS leaves
     vertical page scroll to the browser natively; only a clearly
     horizontal, past-threshold gesture changes the active media.
     ============================================================ */
  function initSwipe(root) {
    var main = root.querySelector(".sleep-gallery__main");
    if (!main || main.hasAttribute("data-sleep-swipe-init")) return;
    main.setAttribute("data-sleep-swipe-init", "true");

    var THRESHOLD = 40;
    var startX = 0;
    var startY = 0;
    var tracking = false;

    main.addEventListener("pointerdown", function (e) {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      startX = e.clientX;
      startY = e.clientY;
      tracking = true;
      try { main.setPointerCapture(e.pointerId); } catch (err) {}
    });

    main.addEventListener("pointerup", function (e) {
      if (!tracking) return;
      tracking = false;
      var deltaX = e.clientX - startX;
      var deltaY = e.clientY - startY;
      if (Math.abs(deltaX) < THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      var state = galleryState(root);
      setActiveIndex(root, state.index + (deltaX < 0 ? 1 : -1));
    });

    main.addEventListener("pointercancel", function () {
      tracking = false;
    });
  }

  /* Keep the mobile/desktop duplicate price nodes in sync after Dawn's
     variant-change round trip (product-info.js only updates
     #price-{sectionId}; the Sleep PDP duplicates that markup for the
     desktop layout). Gallery swap itself no longer waits on this — it
     already ran synchronously from the local JSON on the swatch's own
     change event. */
  function initVariantSync(root) {
    if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") return;
    if (root.hasAttribute("data-sleep-variant-sync")) return;
    root.setAttribute("data-sleep-variant-sync", "true");

    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      if (!event || !event.data) return;
      if (root.dataset.section !== event.data.sectionId) return;

      var sourcePrice = document.getElementById("price-" + event.data.sectionId);
      var desktopPrice = document.getElementById("price-desktop-" + event.data.sectionId);
      if (sourcePrice && desktopPrice) desktopPrice.innerHTML = sourcePrice.innerHTML;
    });
  }

  /* ============================================================
     FREQUENTLY BOUGHT TOGETHER (tapones)
     The toggle is the only add action: on click it Ajax-adds the currently
     selected main variant (from the same hidden input applyVariantCore
     keeps correct) together with the upsell's own real variant
     (data-fbt-variant-id, resolved server-side from
     fbt_product.selected_or_first_available_variant — never hardcoded),
     then refreshes and opens the cart drawer the same way the drawer's own
     upsell "Añadir" buttons do (assets/sleep-cart.js) — reusing Dawn's
     native cart-drawer renderContents(), not a second cart system. A
     failed upsell add never touches the main product's own Add to Cart.
     ============================================================ */
  function initFbt(root) {
    var toggle = root.querySelector("[data-sleep-fbt-toggle]");
    if (!toggle || toggle.hasAttribute("data-sleep-fbt-init")) return;
    toggle.setAttribute("data-sleep-fbt-init", "true");

    toggle.addEventListener("click", function () {
      if (toggle.disabled || toggle.classList.contains("is-loading")) return;
      if (typeof routes === "undefined" || typeof fetchConfig !== "function") return;

      var fbtVariantId = toggle.getAttribute("data-fbt-variant-id");
      if (!fbtVariantId) return;

      var errorEl = root.querySelector("[data-sleep-fbt-error]");
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = "";
      }

      var mainInput = root.querySelector('#product-form-' + root.dataset.section + ' input[name="id"]');
      var mainVariantId = mainInput && mainInput.value;

      var items = [];
      if (mainVariantId) items.push({ id: mainVariantId, quantity: 1 });
      items.push({ id: fbtVariantId, quantity: 1 });

      toggle.classList.add("is-loading");
      toggle.setAttribute("aria-checked", "true");

      fetch(routes.cart_add_url + ".js", {
        ...fetchConfig("javascript"),
        body: JSON.stringify({
          items: items,
          sections: ["cart-drawer", "cart-icon-bubble"],
          sections_url: window.location.pathname,
        }),
      })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data && data.status) throw new Error(data.description || data.message || "Cart add failed");
          var drawer = document.querySelector("cart-drawer");
          if (drawer && typeof drawer.renderContents === "function" && data.sections) {
            drawer.renderContents(data);
          }
          if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: "sleep-pdp-fbt", cartData: data });
          }
        })
        .catch(function (e) {
          console.error("Sleep FBT add failed:", e);
          toggle.setAttribute("aria-checked", "false");
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = "No se ha podido añadir. Inténtalo de nuevo.";
          }
        })
        .finally(function () {
          toggle.classList.remove("is-loading");
        });
    });
  }

  function cardStep(track) {
    var first = track.querySelector(":scope > *");
    if (!first) return track.clientWidth;
    var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 14);
    return first.getBoundingClientRect().width + gap;
  }

  function initCarousels(root) {
    root.querySelectorAll(".sleep-carousel:not([data-sleep-init])").forEach(function (track) {
      track.setAttribute("data-sleep-init", "true");
      var name = track.getAttribute("data-carousel-name");
      if (!name) return;

      var prevBtn = root.querySelector('[data-carousel-prev="' + name + '"]');
      var nextBtn = root.querySelector('[data-carousel-next="' + name + '"]');

      if (prevBtn) prevBtn.addEventListener("click", function () {
        track.scrollBy({ left: -cardStep(track), behavior: "smooth" });
      });
      if (nextBtn) nextBtn.addEventListener("click", function () {
        track.scrollBy({ left: cardStep(track), behavior: "smooth" });
      });

      var scrub = root.querySelector('[data-scrub-for="' + name + '"]');
      function onScroll() {
        var max = track.scrollWidth - track.clientWidth - 2;
        if (prevBtn) prevBtn.disabled = track.scrollLeft <= 2;
        if (nextBtn) nextBtn.disabled = track.scrollLeft >= max;
        if (scrub) {
          var thumb = scrub.querySelector(".sleep-scrub__thumb");
          var scrollMax = track.scrollWidth - track.clientWidth;
          var pct = scrollMax > 0 ? track.scrollLeft / scrollMax : 0;
          var thumbWidth = Math.max(15, (track.clientWidth / track.scrollWidth) * 100);
          thumb.style.width = thumbWidth + "%";
          thumb.style.transform = "translateX(" + (pct * (100 / thumbWidth - 1) * 100) + "%)";
        }
      }
      track.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll);
      onScroll();

      var isDown = false;
      var startX, startScroll;
      track.addEventListener("pointerdown", function (e) {
        isDown = true;
        startX = e.clientX;
        startScroll = track.scrollLeft;
        track.setPointerCapture(e.pointerId);
      });
      track.addEventListener("pointermove", function (e) {
        if (!isDown) return;
        track.scrollLeft = startScroll - (e.clientX - startX);
      });
      ["pointerup", "pointercancel", "pointerleave"].forEach(function (ev) {
        track.addEventListener(ev, function () { isDown = false; });
      });
    });
  }

  function initShowMore(root) {
    root.querySelectorAll("[data-sleep-show-more]:not([data-sleep-init])").forEach(function (btn) {
      btn.setAttribute("data-sleep-init", "true");
      btn.addEventListener("click", function () {
        var hiddenReviews = root.querySelectorAll(".sleep-review--extra[hidden]");
        hiddenReviews.forEach(function (r) { r.removeAttribute("hidden"); });
        if (root.querySelectorAll(".sleep-review--extra[hidden]").length === 0) {
          btn.style.display = "none";
        }
      });
    });
  }

  function initQuantityRadius(root) {
    root.querySelectorAll(".sleep-quantity__box").forEach(function (box) {
      var input = box.querySelector(".quantity__input");
      if (!input) return;
    });
  }

  function init(root) {
    root = root || document;
    root.querySelectorAll(".sleep-pdp").forEach(function (scope) {
      initAccordions(scope);
      initGallery(scope);
      initFbt(scope);
      initCarousels(scope);
      initShowMore(scope);
      initQuantityRadius(scope);
      initVariantSync(scope);
    });
  }

  document.addEventListener("DOMContentLoaded", function () { init(document); });
  document.addEventListener("shopify:section:load", function (event) { init(event.target); });

  /* The cart drawer replaces its own innerHTML wholesale on every Ajax cart
     mutation (quantity change, remove, gift sync, upsell add) — none of which
     fire shopify:section:load. Re-run init so drawer carousels/accordions
     keep working after each update instead of only on first paint. */
  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      init(document);
    });
  }
})();
